import Box from '@app/components/common/Box';
import Grid from '@app/components/common/Grid';
import Typography from '@app/components/common/Typography';
import { formatNumber } from '@app/helpers/format';
import useItemChangeColor from '@app/hooks/useItemChangeColor';
import {
  selectChangeMassUpdateDataSelectionCriteria,
  selectFinalDiffObject,
} from '@app/state/menu/menuSelectors';
import {
  IComboDiffObject,
  TChange,
  TChangeItems,
  TComboMenuTypeVersionDiff,
  TInheritedTaxesDiff,
  TItemGroupDiff,
  formatMoney,
} from '@westondev/tableturn-core';
import { ParseKeys } from 'i18next';
import { Fragment, useMemo, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import HeaderDiffModal from '../../common/HeaderDiffModal';
import MenuTypeVersionsButtons from '../../common/MenuTypeVersionsButtons';
import TextAreaChange from '../../common/TextAreaChange';
import RenderChange from '../../SettingsDiffConfirmationModal/common/RenderChange';
import { specialsDiffModalStyles } from '../SpecialsDiffModal/styles';
import ItemGroupsDiff from './ItemGroupsDiff';
import ItemPricesDiff from './ItemPricesDiff';

interface CombosDiffModalProps {
  selectedMenuTypeVersion: number;
  setSelectedMenuTypeVersion: Dispatch<SetStateAction<number>>;
}

const CombosDiffModal = ({
  selectedMenuTypeVersion,
  setSelectedMenuTypeVersion,
}: CombosDiffModalProps) => {
  // Redux
  const finalDiffObject = useSelector(selectFinalDiffObject);

  const massUpdateSelectionCriteria = useSelector(
    selectChangeMassUpdateDataSelectionCriteria,
  );

  // local state
  const { t } = useTranslation();

  const { borderContainerColor } = useItemChangeColor();

  const formattedDifferences = finalDiffObject as IComboDiffObject;

  const categoriesSubcategoriesDiff = useMemo(
    () =>
      formattedDifferences.menuTypeVersions[
        selectedMenuTypeVersion
      ]?.sections.categoriesSubcategories.map(catSubCatChange => ({
        action: catSubCatChange.action,
        id: catSubCatChange.id,
        value: catSubCatChange.value.categoryName,
        subcategoryValue: catSubCatChange.value.subcategoryName,
      })),
    [formattedDifferences, selectedMenuTypeVersion],
  );

  const renderMenuTypeVersionsButtons = (
    menuTypeVersions: TComboMenuTypeVersionDiff[],
  ) => {
    return (
      <MenuTypeVersionsButtons
        menuTypeVersions={menuTypeVersions}
        selectedMenuTypeVersion={selectedMenuTypeVersion}
        onPress={setSelectedMenuTypeVersion}
      />
    );
  };

  const formatValue = (value: any, field: string, textValue?: string) => {
    if (
      typeof value === 'boolean' &&
      !(!!massUpdateSelectionCriteria && field === 'ageRestricted')
    ) {
      // if value is boolean return yes, no
      if (field === 'active') {
        return value ? t('commonTexts.active') : t('commonTexts.inactive');
      } else return value ? t('commonTexts.yes') : t('commonTexts.no');
    } else if (
      [
        'ageRestricted',
        'defaultModifierGroupPricingVersionId',
        'courseId',
        'actionWhenCurrentStockZero',
        'suggestiveOrderingItemId',
        'allModsBehavior',
        'unit',
        'categoryId',
        'subcategoryId',
      ].includes(field)
    ) {
      return textValue;
    } else if (field === 'suggestiveOrderingItemPrice') {
      if (value === null) {
        return textValue;
      }
      return formatMoney(null, value);
    } else if (typeof value === 'number') {
      // if value is number, format the number depending on the field
      if (
        [
          'itemPrice',
          'onlinePrice',
          'deliveryPrice',
          'toGoPrice',
          'includedMasterPrice',
          'additionalMasterPrice',
          'includedLightPrice',
          'includedRegularPrice',
          'includedExtraPrice',
          'additionalLightPrice',
          'additionalRegularPrice',
          'additionalExtraPrice',
          'defaultSelectionPrice',
        ].includes(field)
      ) {
        if (textValue === 'percent') return value + '%';
        return formatMoney(null, value);
      } else if (field === 'prepTime') {
        return `${formatNumber(Number(value), {
          delimiter: ',',
        })} min`;
      } else return value;
    } else if (typeof value === 'string') {
      if (field === 'customizerShape') {
        return textValue;
      } else return value;
    } else return value;
  };

  const renderChange = (change: TChangeItems) => {
    return (
      <RenderChange
        formatValue={formatValue}
        change={change}
        renderAsString={
          !!change.textValue ||
          change.field === 'imageUrl' ||
          change.field === 'secondaryImageUrl'
        }
        isImage={
          change.field === 'imageUrl' || change.field === 'secondaryImageUrl'
        }
        colorFields={['backgroundColor', 'textColor', 'kdsColor']}
        defaultColor={{}}
        textCsx={{ fontSize: '14px' }}
      />
    );
  };

  const renderPrices = () => {
    const prices =
      formattedDifferences.menuTypeVersions[selectedMenuTypeVersion].sections
        .pricing;
    return (
      <ItemPricesDiff
        prices={prices}
        formatValue={formatValue}
        isMassUpdate={!!massUpdateSelectionCriteria}
        hideTotals
      />
    );
  };

  const renderItemGroups = (itemGroups: TItemGroupDiff[]) => {
    return <ItemGroupsDiff itemGroups={itemGroups} formatValue={formatValue} />;
  };

  return (
    <>
      {formattedDifferences && !massUpdateSelectionCriteria && (
        <>
          {formattedDifferences.masterChanges.length > 0 && (
            <>
              <HeaderDiffModal
                name={t('app.modals.diffModal.sectionTitles.commonChanges')}
                color="black"
              />
              <Grid columnGap={{ mb: 20, md: 60 }} rowGap={10}>
                {formattedDifferences.masterChanges
                  .filter(
                    change =>
                      ![
                        'locationIds',
                        'linkedModifierItems',
                        'reportingGroupIds',
                      ].includes(change.field),
                  )
                  .map(change => (
                    <Grid.Item
                      mb={12}
                      sm={6}
                      md={4}
                      lg={4}
                      key={`master_change_${change.field}`}>
                      {renderChange(change)}
                    </Grid.Item>
                  ))}
              </Grid>
              <Box>
                {formattedDifferences.masterChanges
                  .filter(change => change.field === 'reportingGroupIds')
                  .map(renderChange)}
              </Box>
              <Box>
                {formattedDifferences.masterChanges
                  .filter(change => change.field === 'locationIds')
                  .map(renderChange)}
              </Box>
              <Box>
                {formattedDifferences.masterChanges
                  .filter(change => change.field === 'linkedModifierItems')
                  .map(renderChange)}
              </Box>
            </>
          )}
        </>
      )}
      {formattedDifferences &&
        formattedDifferences.menuTypeVersions.length > 0 && (
          <>
            {massUpdateSelectionCriteria && (
              <HeaderDiffModal
                name={t('app.modals.diffModal.sectionTitles.massUpdateChanges')}
                color="black"
                containerCsx={{ marginTop: '20px' }}
              />
            )}
            {renderMenuTypeVersionsButtons(
              formattedDifferences.menuTypeVersions,
            )}
            <Box
              csx={[
                specialsDiffModalStyles,
                {
                  backgroundColor: 'white',
                  border: `1px solid ${borderContainerColor}`,
                  marginBottom: '5px',
                },
              ]}>
              {formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                .action === 'remove' && (
                <Typography>
                  {t('app.modals.itemsDiff.menuType.menuTypeRemoved')}
                </Typography>
              )}
              {formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                .sections.basic.length > 0 && (
                <>
                  <HeaderDiffModal
                    name={t('app.modals.diffModal.sectionTitles.basic')}
                  />
                  <Grid columnGap={{ mb: 20, md: 60 }} rowGap={10}>
                    {formattedDifferences.menuTypeVersions[
                      selectedMenuTypeVersion
                    ].sections.basic.map(change => (
                      <Grid.Item
                        mb={12}
                        sm={6}
                        md={4}
                        lg={4}
                        key={`basic_change_${change.field}`}>
                        {renderChange(change)}
                      </Grid.Item>
                    ))}
                  </Grid>
                </>
              )}

              {categoriesSubcategoriesDiff &&
                categoriesSubcategoriesDiff.length > 0 && (
                  <>
                    <HeaderDiffModal
                      name={t(
                        'app.modals.diffModal.sectionTitles.categoriesSubcategories',
                      )}
                    />

                    <Box>
                      {renderChange({
                        field: 'categoriesSubcategories',
                        label: t(
                          'menuScreen.itemDetails.categoriesSubcategories.title',
                        ),
                        value:
                          categoriesSubcategoriesDiff as unknown as TChange['value'],
                      })}
                    </Box>
                  </>
                )}

              {formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                .sections.register.length > 0 && (
                <>
                  <HeaderDiffModal
                    name={t('app.modals.itemsDiff.sectionTitles.register')}
                  />
                  <Grid columnGap={{ mb: 20, md: 60 }} rowGap={10}>
                    {formattedDifferences.menuTypeVersions[
                      selectedMenuTypeVersion
                    ].sections.register
                      .filter(change => !change.field.includes('Color'))
                      .map(change => (
                        <Grid.Item
                          mb={12}
                          sm={6}
                          md={4}
                          lg={4}
                          key={`register_change_${change.field}`}>
                          {renderChange(change)}
                        </Grid.Item>
                      ))}
                  </Grid>
                  <Grid columnGap={{ mb: 20, md: 60 }} rowGap={10}>
                    {formattedDifferences.menuTypeVersions[
                      selectedMenuTypeVersion
                    ].sections.register
                      .filter(change => change.field.includes('Color'))
                      .map(change => (
                        <Grid.Item
                          mb={12}
                          sm={6}
                          md={4}
                          lg={4}
                          key={`register_change_${change.field}`}>
                          {renderChange(change)}
                        </Grid.Item>
                      ))}
                  </Grid>
                </>
              )}
              {formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                .sections.pricing.length > 0 && (
                <>
                  <HeaderDiffModal
                    name={t('app.modals.itemsDiff.sectionTitles.pricing')}
                  />
                  {renderPrices()}
                </>
              )}
              {formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                .sections.inventory.length > 0 && (
                <>
                  <HeaderDiffModal
                    name={t('app.modals.itemsDiff.sectionTitles.inventory')}
                  />
                  <Grid columnGap={{ mb: 20, md: 60 }} rowGap={10}>
                    {formattedDifferences.menuTypeVersions[
                      selectedMenuTypeVersion
                    ].sections.inventory.map(change => (
                      <Grid.Item
                        mb={12}
                        sm={6}
                        md={4}
                        lg={4}
                        key={`inventory_change_${change.field}`}>
                        {renderChange(change)}
                      </Grid.Item>
                    ))}
                  </Grid>
                </>
              )}
              {formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                .sections.moreInfo.length > 0 && (
                <>
                  <HeaderDiffModal
                    name={t(
                      'app.modals.diffModal.sectionTitles.moreInformation',
                    )}
                  />
                  <Grid columnGap={{ mb: 20, md: 60 }} rowGap={10}>
                    {formattedDifferences.menuTypeVersions[
                      selectedMenuTypeVersion
                    ].sections.moreInfo.map(change => (
                      <Grid.Item key={`change-${change.field}`}>
                        <Fragment>
                          {change.value ? (
                            <TextAreaChange change={change} />
                          ) : (
                            renderChange(change)
                          )}
                        </Fragment>
                      </Grid.Item>
                    ))}
                  </Grid>
                </>
              )}
              {formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                .sections.associations.length > 0 && (
                <>
                  <HeaderDiffModal
                    name={t('app.modals.diffModal.sectionTitles.associations')}
                  />
                  <Grid columnGap={{ mb: 20, md: 60 }} rowGap={10}>
                    {formattedDifferences.menuTypeVersions[
                      selectedMenuTypeVersion
                    ].sections.associations.map(change => (
                      <Grid.Item
                        mb={12}
                        sm={6}
                        md={4}
                        lg={4}
                        key={`associations_change_${change.field}`}>
                        {renderChange(change)}
                      </Grid.Item>
                    ))}
                  </Grid>
                </>
              )}
              {Object.keys(
                formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                  .sections.taxes || {},
              ).length > 0 && (
                <>
                  <HeaderDiffModal
                    name={t('app.modals.diffModal.sectionTitles.taxes')}
                  />
                  <Box>
                    {Object.keys(
                      formattedDifferences.menuTypeVersions[
                        selectedMenuTypeVersion
                      ].sections.taxes,
                    ).map(key => (
                      <Box key={`taxes-${key}`}>
                        {renderChange({
                          field: 'inheritedTaxesIds',
                          label: t(
                            `app.modals.itemsDiff.taxes.${key}` as ParseKeys,
                          ),
                          value:
                            formattedDifferences.menuTypeVersions[
                              selectedMenuTypeVersion
                            ].sections.taxes[
                              key as keyof TInheritedTaxesDiff
                            ] || [],
                        })}
                      </Box>
                    ))}
                  </Box>
                </>
              )}

              {formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                .sections.itemGroups.length > 0 && (
                <>
                  <HeaderDiffModal
                    name={t('app.modals.combosDiff.sectionTitles.itemGroups')}
                  />

                  {renderItemGroups(
                    formattedDifferences.menuTypeVersions[
                      selectedMenuTypeVersion
                    ].sections.itemGroups,
                  )}
                </>
              )}
            </Box>
          </>
        )}
    </>
  );
};

export default CombosDiffModal;
